import type { AgendaItem, User, UserWRole } from "@repo/types";
import { DatePicker } from "@repo/ui/components/Base/DatePicker";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { TextArea } from "@repo/ui/components/Base/TextArea";
import { useMemo } from "react";
import { AgendaItemVotesForm } from "./AgendaItemVotesForm";
import { ApprovalButtons } from "../common/ApprovalButtons";
import { Formik } from "formik";
import { Button } from "@repo/ui/components/Base/Button";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";
import { useUserList } from "@/hooks/queryHooks/userHooks";
import { useGetTeam } from "@/hooks/queryHooks/teamHooks";

export const AgendaItemReviewForm = ({
  disabled = false,
  tenantId,
  user,
  agendaItem,
  cancelButtonLabel = "Cancel",
  handleCancel,
  handleSave,
  withinDialog = false,
}: {
  disabled?: boolean;
  tenantId: string;
  user: User;
  agendaItem: AgendaItem;
  cancelButtonLabel?: string;
  handleCancel?: () => void;
  handleSave?: (agendaItem: AgendaItem) => void;
  withinDialog?: boolean;
}) => {
  const { data: team } = useGetTeam(tenantId, agendaItem.teamId ?? "");
  const { data: users } = useUserList(tenantId);

  const teamUsers = useMemo(
    () =>
      team && users
        ? (team.teamMembers
            .map((member) =>
              (member.class ?? "voting") === "voting"
                ? users.find((user) => user.id === member.userId)
                : null
            )
            .filter((user) => !!user) as UserWRole[])
        : null,
    [team, users]
  );

  const isUserApproval = agendaItem.approver?.approverType === "user";

  const teamMemberOptions =
    teamUsers?.map((user) => (
      <SelectItem key={user.id} value={user.id}>
        {user.firstName} {user.lastName} ({user.email})
      </SelectItem>
    )) ?? [];

  return (
    <Formik
      initialValues={agendaItem}
      onSubmit={async (values) => {
        if (!tenantId) {
          console.log("No tenant ID", tenantId);
          return;
        }

        const updatedAgendaItem = await agendaItemRepo.update(
          tenantId,
          values.id,
          { ...values }
        );

        if (updatedAgendaItem) handleSave?.(updatedAgendaItem);
      }}
    >
      {({
        values,
        isSubmitting,
        handleSubmit,
        handleChange,
        handleBlur,
        setFieldValue,
      }) => {
        const footer = (
          <>
            <Button variant="destructive" onClick={handleCancel}>
              {cancelButtonLabel}
            </Button>

            <Button type="submit" disabled={isSubmitting}>
              Save
            </Button>
          </>
        );

        const form = (
          <div className="flex flex-col gap-y-3">
            <h2 className="text-xl font-bold">
              Agenda Item: {agendaItem.name}
            </h2>
            {agendaItem.description && <p>{agendaItem.description}</p>}

            {!isUserApproval && (
              <>
                <SelectInput
                  disabled={disabled}
                  label="Motion moved by"
                  placeholder="Select a team member"
                  value={values.review?.moved ?? ""}
                  onValueChange={(value) => {
                    setFieldValue("review.moved", value);
                  }}
                  options={[
                    <SelectItem key="null" value="null">
                      N/A
                    </SelectItem>,
                    <SelectItem key="none" value="none">
                      Not Moved
                    </SelectItem>,
                    ...teamMemberOptions,
                  ]}
                />
                <SelectInput
                  disabled={disabled}
                  label="Motion seconded by"
                  placeholder="Select a team member"
                  value={values.review?.seconded ?? ""}
                  onValueChange={(value) => {
                    setFieldValue("review.seconded", value);
                  }}
                  options={[
                    <SelectItem key="null" value="null">
                      N/A
                    </SelectItem>,
                    <SelectItem key="none" value="none">
                      Not Seconded
                    </SelectItem>,
                    ...teamMemberOptions,
                  ]}
                />
              </>
            )}

            <TextArea
              disabled={disabled}
              label={isUserApproval ? "Review Comments" : "Item Discussion"}
              id="reviewComments"
              name="reviewComments"
              value={values.review?.comments ?? ""}
              onChange={handleChange("review.comments")}
              onBlur={handleBlur}
            />

            {!isUserApproval && (
              <AgendaItemVotesForm
                disabled={disabled}
                votes={values.review?.votes}
                votingUsers={teamUsers ?? []}
                onVotesChange={(votes) => {
                  const review = Object.assign({}, values.review);
                  review.votes = Object.assign({}, review.votes, votes);
                  setFieldValue("review", review);
                }}
              />
            )}

            <ApprovalButtons
              disabled={disabled}
              label="Approval"
              value={values.review?.status ?? "pending"}
              onValueChange={(value) => {
                setFieldValue("review.status", value);
                setFieldValue("review.reviewedBy", user.id);
              }}
            />

            {values.review?.status === "deferred" && (
              <div className="flex flex-row justify-start gap-4 items-center pt-4">
                <DatePicker
                  id="requestedApprovalDate"
                  label="Review Deferred Date"
                  disabled={disabled}
                  date={values.requestedApprovalDate}
                  onDateChange={(date) =>
                    setFieldValue("requestedApprovalDate", date)
                  }
                />
              </div>
            )}

            {!withinDialog && (
              <div className="flex flex-row justify-between gap-4">
                {footer}
              </div>
            )}
          </div>
        );

        return (
          <form onSubmit={handleSubmit}>
            {withinDialog ? (
              <>
                <DialogBody>{form}</DialogBody>
                <DialogFooter className="justify-between">
                  {footer}
                </DialogFooter>
              </>
            ) : (
              form
            )}
          </form>
        );
      }}
    </Formik>
  );
};
